import { IUserSettingsClient } from "@multiweb/clients/userSettings";
import { IAssignedRoles, IUserRoleActivity } from "@multiweb/types/contracts";
import { CancellationToken } from "@multiweb/types/multiweb";
import { IRole, IRoles } from "./IRolesAndICompetences";
import { IUserSettingsManager } from "./IUserSettingsManager";

export class UserSettingsManager implements IUserSettingsManager {
    //#region Fields
    private _roles: IRoles = {};
    private readonly _userSettingsClient: IUserSettingsClient;
    //#endregion Fields

    //#region Constructors
    constructor(userSettingClient: IUserSettingsClient) {
        this._userSettingsClient = userSettingClient;
    }
    //#endregion Constructors

    //#region Methods
    public async saveData(cancellationToken: CancellationToken = CancellationToken.none): Promise<void> {
        await this._userSettingsClient.saveActiveRoles(this._createRoleSettings(), cancellationToken);
    }

    public async getRoles(cancellationToken: CancellationToken = CancellationToken.none): Promise<IRoles> {
        await this._loadRolesAndCompetences(cancellationToken);
        return this._roles;
    }

    public async getActiveRolesIds(cancellationToken: CancellationToken = CancellationToken.none): Promise<string[]> {
        const userRoles: IRole[] = Object.values(await this.getRoles(cancellationToken));
        return userRoles.filter(userRole => userRole.active).map(activeUserRole => activeUserRole.id);
    }

    public async setActiveUserRoleRanges(cancellationToken: CancellationToken = CancellationToken.none): Promise<void> {
        const userRoles: IRole[] = Object.values(await this.getRoles(cancellationToken));
        await this._userSettingsClient.setActiveUserRoleRanges(userRoles);
    }

    public updateRoleActivity(id: string, value: boolean): void {
        if (this._roles[id]) {
            this._roles[id].active = value;
        }
    }

    public updateCompetenceActivity(roleId: string, competenceId: string, value: boolean): void {
        const competence = this._roles[roleId]?.competenceRanges.find(i => i.id == competenceId);
        if (competence) {
            competence.active = value;
        }
    }

    private async _loadRolesAndCompetences(cancellationToken: CancellationToken): Promise<void> {
        const userData = await this._userSettingsClient.getActiveRoles(cancellationToken);
        const roles = await this._userSettingsClient.getAssignedRoles(cancellationToken);

        if (!userData) {
            // data nejsou dostupná - načtu všechny přidělené role a nastavím jako aktivní
            this._fillRoleList(roles);
            await this._userSettingsClient.saveActiveRoles(this._createRoleSettings(), cancellationToken);
        } else {
            // načtu nastavení z dat
            this._fillRoleListFromData(roles, userData);
        }
    }

    private _fillRoleList(roles: IAssignedRoles): void {
        for (const roleRow in roles) {
            const role = roles[roleRow] as IRole;
            role.active = true;
            role.competenceRanges = this._fillCompetenceRanges(role);
            this._roles[role.id] = role;
        }
    }

    /**
     * Načte kompetenční okruhy z dostupných rolí a dat
     * @param role ze které se načtou kompetenční okruhy
     */
    private _fillCompetenceRanges(role: IRole, userData: readonly IUserRoleActivity[] = null): IRole[] {
        const competenceRangeList: IRole[] = [];
        const settingsRole = userData?.find(item => item.id == role.id);

        for (const competence of role.competenceRanges) {
            competence.active = settingsRole?.ranges?.find(item => item.id == competence.id)?.active;

            if (competence.active == null) {
                competence.active = true;
            }

            competenceRangeList.push(competence);
        }

        return competenceRangeList;
    }

    private _fillRoleListFromData(roles: IAssignedRoles, userData: readonly IUserRoleActivity[]): void {
        for (const roleRow in roles) {
            const role = roles[roleRow] as IRole;
            role.active = true;

            const roleItem = userData?.filter(item => item.id == role.id);

            if (roleItem?.length) {
                role.active = roleItem[0].active;
            }

            role.competenceRanges = this._fillCompetenceRanges(role, userData);
            this._roles[role.id] = role;
        }
    }

    private _createRoleSettings(): IUserRoleActivity[] {
        const userRoles: IUserRoleActivity[] = [];
        for (const [roleID, role] of Object.entries(this._roles)) {
            const rangeArr: IUserRoleActivity[] = [];
            if (role.competenceRanges) {
                for (const range of role.competenceRanges) {
                    const rangeSettings: IUserRoleActivity = {
                        id: range.id,
                        active: range.active,
                    };
                    rangeArr.push(rangeSettings);
                }
            }

            const roleSettings: IUserRoleActivity = {
                id: roleID,
                active: role.active,
                ranges: rangeArr.length ? rangeArr : null,
            };

            userRoles.push(roleSettings);
        }
        return userRoles;
    }
    //#endregion Methods
}
